/**
 * Admin Fulfillment Dashboard - Monitor all orders and fulfillment status
 * Real-time overview of order processing, packaging, and delivery
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useOrderNotification } from "../hooks/useOrderNotification";
import {
  ArrowLeft,
  Volume2,
  VolumeX,
  Package,
  Truck,
  CheckCircle,
  Clock,
  RefreshCw,
  Search,
  Filter,
  Box,
  MapPin,
  User,
  QrCode,
  AlertCircle,
  TrendingUp,
  LogOut,
  Activity,
  Printer,
  Download,
  Bell
} from "lucide-react";

const statusColors = {
  pending: "bg-amber-100 text-amber-700",
  processing: "bg-blue-100 text-blue-700",
  packed: "bg-purple-100 text-purple-700",
  in_queue: "bg-indigo-100 text-indigo-700",
  assigned: "bg-cyan-100 text-cyan-700",
  picked_up: "bg-teal-100 text-teal-700",
  in_transit: "bg-orange-100 text-orange-700",
  at_pudo: "bg-pink-100 text-pink-700",
  delivered: "bg-green-100 text-green-700",
  returned: "bg-red-100 text-red-700",
  cancelled: "bg-gray-100 text-gray-700"
};

const statusIcons = {
  pending: Clock,
  processing: Activity,
  packed: Box,
  in_queue: Package,
  assigned: User,
  picked_up: Truck,
  in_transit: Truck,
  at_pudo: MapPin,
  delivered: CheckCircle,
  returned: AlertCircle,
  cancelled: AlertCircle
};

const StatCard = ({ label, value, icon: Icon, color }) => {
  const colorClasses = {
    amber: "bg-amber-900/50 text-amber-400",
    blue: "bg-blue-900/50 text-blue-400",
    purple: "bg-purple-900/50 text-purple-400",
    orange: "bg-orange-900/50 text-orange-400",
    green: "bg-green-900/50 text-green-400",
    gray: "bg-gray-700 text-gray-300"
  };

  return (
    <div className="bg-gray-800 rounded-xl p-4 shadow-lg border border-gray-700" data-testid={"stat-" + label.toLowerCase()}>
      <div className="flex items-center gap-3 mb-2">
        <div className={"p-2 rounded-lg " + colorClasses[color]}>
          <Icon className="h-4 w-4" />
        </div>
        <span className="text-gray-300 text-sm">{label}</span>
      </div>
      <p className="text-2xl font-bold text-white">{value}</p>
    </div>
  );
};

const AdminFulfillmentDashboard = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [orders, setOrders] = useState([]);
  const [queue, setQueue] = useState([]);
  const [stats, setStats] = useState({
    pending: 0,
    processing: 0,
    packed: 0,
    in_transit: 0,
    delivered: 0,
    total: 0
  });
  const [filter, setFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [soundEnabled, setSoundEnabled] = useState(true);
  
  // Order notification hook
  const { checkForNewOrders, playSound, volume, setVolume } = useOrderNotification({
    enabled: true,
    soundEnabled: soundEnabled,
    toastEnabled: true
  });
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [pendingRes, queueRes] = await Promise.all([
        api.get("/fulfillment/orders/pending?limit=100").catch(() => ({ data: { orders: [] } })),
        api.get("/fulfillment/queue?status=waiting").catch(() => ({ data: { queue: [] } })),
      ]);

      const pendingOrders = pendingRes.data?.orders || [];
      const queueItems = queueRes.data?.queue || [];

      setOrders(pendingOrders);
      setQueue(queueItems);

      const statusCounts = pendingOrders.reduce((acc, order) => {
        const status = order.fulfillment_status || "pending";
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {});

      setStats({
        pending: statusCounts.pending || 0,
        processing: statusCounts.processing || 0,
        packed: statusCounts.packed || 0,
        in_transit: (statusCounts.in_transit || 0) + (statusCounts.picked_up || 0),
        delivered: statusCounts.delivered || 0,
        total: pendingOrders.length
      });
      
      // Check for new orders and play notification sound
      checkForNewOrders(pendingOrders.length);

    } catch (error) {
      console.error("Failed to fetch fulfillment data:", error);
      toast.error("Failed to load fulfillment data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
    toast.success("Dashboard refreshed");
  };

  const handleLogout = () => {
    logout();
    toast.success("Logged out successfully");
    navigate("/login");
  };

  const handleProcessOrder = async (orderId) => {
    try {
      await api.post("/fulfillment/orders/process", { order_id: orderId });
      toast.success("Order processing started");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to process order");
    }
  };


  // Print order details
  const handlePrintOrder = (order) => {
    const printContent = `
      <html>
        <head>
          <title>Order Details - ${order.id}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            .header { border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 20px; }
            .section { margin-bottom: 15px; }
            .label { font-weight: bold; color: #666; }
            .items { border: 1px solid #ddd; padding: 10px; margin-top: 10px; }
            .item { padding: 5px 0; border-bottom: 1px solid #eee; }
            .total { font-size: 18px; font-weight: bold; margin-top: 20px; }
            @media print { body { padding: 0; } }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Order #${order.id?.slice(0, 8)}</h1>
            ${order.tracking_code ? "<p>Tracking: " + order.tracking_code + "</p>" : ""}
          </div>
          <div class="section">
            <p class="label">Customer:</p>
            <p>${order.customer_name || "N/A"}</p>
          </div>
          <div class="section">
            <p class="label">Delivery Address:</p>
            <p>${order.delivery_address?.street || ""}</p>
            <p>${order.delivery_address?.city || ""}, ${order.delivery_address?.parish || ""}</p>
          </div>
          <div class="total">Total: J$${order.total_amount?.toLocaleString() || 0}</div>
          <p style="margin-top: 40px; font-size: 12px; color: #999;">Printed: ${new Date().toLocaleString()}</p>
        </body>
      </html>
    `;
    const printWindow = window.open("", "_blank");
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  // Mark order ready for pickup and notify drivers
  const handleReadyForPickup = async (orderId) => {
    try {
      await api.post("/fulfillment/orders/pack", { order_id: orderId });
      toast.success("Order marked ready for pickup! Drivers notified.");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update order");
    }
  };

  // Print QR code shipping label
  const handlePrintQRLabel = (order) => {
    const qrDataUrl = "https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=" + encodeURIComponent(order.tracking_code || order.id);
    const labelContent = `
      <html>
        <head>
          <title>Shipping Label</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; max-width: 400px; margin: 0 auto; }
            .label-box { border: 3px solid #000; padding: 20px; }
            .header { text-align: center; border-bottom: 2px solid #000; padding-bottom: 10px; margin-bottom: 15px; }
            .qr-section { text-align: center; margin: 20px 0; }
            .qr-section img { width: 150px; height: 150px; }
            .tracking { font-size: 20px; font-weight: bold; text-align: center; letter-spacing: 2px; }
            .to { background: #f0f0f0; padding: 10px; margin-top: 20px; }
            .label { font-size: 12px; color: #666; }
          </style>
        </head>
        <body>
          <div class="label-box">
            <div class="header"><h2>LIANTIAO</h2><p style="font-size: 12px;">Jamaica Delivery</p></div>
            <div class="qr-section"><img src="${qrDataUrl}" alt="QR Code" /></div>
            <div class="tracking">${order.tracking_code || order.id?.slice(0, 8)}</div>
            <div class="to">
              <p class="label">DELIVER TO:</p>
              <p>${order.customer_name || "Customer"}</p>
              <p>${order.delivery_address?.street || ""}</p>
              <p>${order.delivery_address?.city || ""}, ${order.delivery_address?.parish || ""}</p>
              ${order.pudo_name ? "<p style=\"color: #0066cc;\">PUDO: " + order.pudo_name + "</p>" : ""}
            </div>
          </div>
        </body>
      </html>
    `;
    const printWindow = window.open("", "_blank");
    printWindow.document.write(labelContent);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 500);
  };

  const filteredOrders = orders.filter(order => {
    const matchesFilter = filter === "all" || order.fulfillment_status === filter;
    const matchesSearch = !searchTerm || 
      order.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.tracking_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customer_name?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin h-10 w-10 border-4 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900" data-testid="admin-fulfillment-dashboard">
      <header className="bg-gray-800 border-b border-gray-700 px-4 py-4 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={() => navigate("/admin")}
              className="p-2 hover:bg-gray-700 rounded-full"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-5 w-5 text-gray-300" />
            </Button>
            <div className="flex items-center gap-2">
              <div className="p-2 bg-blue-900/50 rounded-lg">
                <Package className="h-5 w-5 text-blue-400" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white">Fulfillment Dashboard</h1>
                <p className="text-xs text-gray-400">Monitor all orders & deliveries</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-400">Auto-refresh: 30s</span>
            <div className="relative">
              <Button
                variant="ghost"
                onClick={() => {
                  if (soundEnabled) {
                    setShowVolumeSlider(!showVolumeSlider);
                  } else {
                    setSoundEnabled(true);
                    playSound();
                  }
                }}
                onContextMenu={(e) => {
                  e.preventDefault();
                  setSoundEnabled(!soundEnabled);
                }}
                className={"p-2 " + (soundEnabled ? "text-green-400" : "text-gray-500")}
                title={soundEnabled ? "Click to adjust volume, Right-click to mute" : "Click to enable sound"}
                data-testid="sound-toggle-btn"
              >
                {soundEnabled ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
              </Button>
              {showVolumeSlider && soundEnabled && (
                <div className="absolute right-0 top-full mt-2 bg-slate-800 border border-slate-700 rounded-lg p-3 shadow-xl z-50 w-48">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-gray-400">Volume</span>
                    <span className="text-xs text-amber-400 font-mono">{Math.round(volume * 100)}%</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={volume * 100}
                    onChange={(e) => setVolume(parseInt(e.target.value) / 100)}
                    className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-amber-500"
                  />
                  <div className="flex justify-between mt-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => { playSound(); }}
                      className="text-xs text-gray-300 hover:text-white"
                    >
                      Test Sound
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setShowVolumeSlider(false)}
                      className="text-xs text-gray-400"
                    >
                      Close
                    </Button>
                  </div>
                </div>
              )}
            </div>
            <Button
              variant="ghost"
              onClick={handleRefresh}
              disabled={refreshing}
              className="p-2"
              data-testid="refresh-btn"
            >
              <RefreshCw className={"h-5 w-5 text-gray-300 " + (refreshing ? "animate-spin" : "")} />
            </Button>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="text-gray-400 hover:text-white hover:bg-gray-700"
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="stats-section">
          <StatCard label="Pending" value={stats.pending} icon={Clock} color="amber" />
          <StatCard label="Processing" value={stats.processing} icon={Activity} color="blue" />
          <StatCard label="Packed" value={stats.packed} icon={Box} color="purple" />
          <StatCard label="In Transit" value={stats.in_transit} icon={Truck} color="orange" />
          <StatCard label="Delivered" value={stats.delivered} icon={CheckCircle} color="green" />
          <StatCard label="Total" value={stats.total} icon={TrendingUp} color="gray" />
        </div>

        <div className="bg-gray-800 rounded-xl p-4 shadow-lg border border-gray-700">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by Order ID, Tracking Code, or Customer..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="search-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-400" />
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="px-4 py-2 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500"
                data-testid="status-filter"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="packed">Packed</option>
                <option value="in_queue">In Queue</option>
                <option value="assigned">Assigned</option>
                <option value="in_transit">In Transit</option>
                <option value="at_pudo">At PUDO</option>
                <option value="delivered">Delivered</option>
              </select>
            </div>
          </div>
        </div>

        {queue.length > 0 && (
          <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700" data-testid="queue-section">
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <h2 className="font-bold text-white flex items-center gap-2">
                <Truck className="h-5 w-5 text-orange-500" />
                Delivery Queue ({queue.length})
              </h2>
            </div>
            <div className="divide-y divide-gray-100 max-h-60 overflow-y-auto">
              {queue.map((item) => (
                <div key={item.id} className="p-4 hover:bg-gray-700" data-testid={"queue-item-" + item.id}>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-medium text-white">{item.tracking_code}</span>
                      <span className="ml-2 text-sm text-gray-400">→ {item.customer_name}</span>
                    </div>
                    <span className={"text-xs px-2 py-1 rounded-full " + (
                      item.status === "waiting" ? "bg-amber-100 text-amber-700" :
                      item.status === "assigned" ? "bg-blue-100 text-blue-700" :
                      "bg-gray-100 text-gray-700"
                    )}>
                      {item.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700" data-testid="orders-section">
          <div className="p-4 border-b border-gray-700 flex items-center justify-between">
            <h2 className="font-bold text-white flex items-center gap-2">
              <Package className="h-5 w-5 text-blue-500" />
              Orders ({filteredOrders.length})
            </h2>
          </div>
          
          {filteredOrders.length === 0 ? (
            <div className="p-8 text-center">
              <Package className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-400">No orders found</p>
              <p className="text-sm text-gray-400 mt-1">
                {filter !== "all" ? "Try changing the filter" : "Orders will appear here when customers place them"}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredOrders.map((order) => {
                const StatusIcon = statusIcons[order.fulfillment_status] || Clock;
                return (
                  <div 
                    key={order.id} 
                    className="p-4 hover:bg-gray-700 transition-colors"
                    data-testid={"order-" + order.id}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-semibold text-white">
                            #{order.id?.slice(0, 8)}
                          </span>
                          {order.tracking_code && (
                            <span className="text-xs bg-gray-100 text-gray-300 px-2 py-0.5 rounded flex items-center gap-1">
                              <QrCode className="h-3 w-3" />
                              {order.tracking_code}
                            </span>
                          )}
                          <span className={"text-xs px-2 py-1 rounded-full flex items-center gap-1 " + (statusColors[order.fulfillment_status] || "bg-gray-100 text-gray-700")}>
                            <StatusIcon className="h-3 w-3" />
                            {(order.fulfillment_status || "pending").replace(/_/g, " ")}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-400">
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {order.customer_name || "Customer"}
                          </span>
                          <span>{order.items?.length || 0} items</span>
                          <span className="font-medium text-gray-700">
                            J${order.total_amount?.toLocaleString() || 0}
                          </span>
                        </div>
                        {order.delivery_address && (
                          <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {order.delivery_address.street}, {order.delivery_address.city}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {order.fulfillment_status === "pending" && (
                          <Button
                            size="sm"
                            onClick={() => handleProcessOrder(order.id)}
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                            data-testid={"process-" + order.id}
                          >
                            Start Processing
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handlePrintOrder(order)}
                          data-testid={"print-" + order.id}
                          title="Print Order Details"
                        >
                          <Printer className="h-4 w-4" />
                        </Button>
                        {order.fulfillment_status === "processing" && (
                          <Button
                            size="sm"
                            onClick={() => handleReadyForPickup(order.id)}
                            className="bg-green-600 hover:bg-green-700 text-white"
                            data-testid={"ready-" + order.id}
                          >
                            <Bell className="h-4 w-4 mr-1" />
                            Ready
                          </Button>
                        )}
                        {order.tracking_code && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePrintQRLabel(order)}
                            data-testid={"qr-" + order.id}
                            title="Print QR Label"
                          >
                            <QrCode className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate("/admin/fulfillment/order/" + order.id)}
                          data-testid={"view-" + order.id}
                        >
                          View
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminFulfillmentDashboard;
